import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const MILEAGE_RATE_FIELDS = {
  ratePerMile: 0,
  effectiveDate: null,
  source: "", // e.g., "IRS Standard Mileage Rate"

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(MILEAGE_RATE_FIELDS);

// IRS Standard Mileage Rates fallback, used only when no rate is stored in the
// mileageRates collection. Rates are resolved by effective date, so mid-year
// changes (e.g. the 2026 mid-year increase) are represented as separate
// entries. Keep this in sync with functions/mileage.js IRS_MILEAGE_RATES.
export const IRS_MILEAGE_RATES = [
  { effectiveDate: "2024-01-01", ratePerMile: 0.67 },
  { effectiveDate: "2025-01-01", ratePerMile: 0.7 },
  { effectiveDate: "2026-01-01", ratePerMile: 0.7 },
  { effectiveDate: "2026-07-01", ratePerMile: 0.76 },
];
deepFreeze(IRS_MILEAGE_RATES);

// Normalize a rate-like object (stored doc data or fallback entry) to the Date
// on which it takes effect. Older docs may only carry a `year`, in which case
// the rate is treated as effective January 1st of that year.
export function rateEffectiveDate(rate) {
  if (!rate) return null;
  if (rate.effectiveDate) return timestampToDate(rate.effectiveDate);
  if (rate.year) return new Date(rate.year, 0, 1);
  return null;
}

// Given a list of rate-like objects, return the one in effect on the reference
// date (the most recent whose effective date is on or before it), or null.
export function resolveRateForDate(rates, referenceDate = new Date()) {
  const ref = timestampToDate(referenceDate) || new Date();
  const applicable = (rates || [])
    .map((rate) => ({ rate, effective: rateEffectiveDate(rate) }))
    .filter(
      ({ effective }) => effective && effective.getTime() <= ref.getTime()
    )
    .sort((a, b) => b.effective.getTime() - a.effective.getTime());
  return applicable.length ? applicable[0].rate : null;
}

export default class MileageRate extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, MILEAGE_RATE_FIELDS);

    // Convert to Date
    data.effectiveDate = timestampToDate(data.effectiveDate);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: MileageRate.canBeViewedBy,
      create: MileageRate.canBeCreatedBy,
      edit: MileageRate.canBeEditedBy,
      delete: MileageRate.canBeDeletedBy,
    });
  }

  // Permissions - all employees can view (rates drive reimbursement math);
  // only admins and accounting can manage them.

  static canBeViewedBy(user) {
    return user.isEmployee;
  }

  static canBeCreatedBy(user) {
    return user.isAdmin || user.hasRole("accounting");
  }

  static canBeEditedBy(user) {
    return user.isAdmin || user.hasRole("accounting");
  }

  static canBeDeletedBy(user) {
    return user.isAdmin || user.hasRole("accounting");
  }

  // Database

  get path() {
    return MileageRate.getPath(this.id);
  }

  static getPath(id) {
    if (id) {
      return `mileageRates/${id}`;
    }
    return "mileageRates";
  }

  static getStateID(path) {
    const match = path.match(/^mileageRates\/([^/]+)$/);
    if (match) {
      return match[1];
    }
    return null;
  }

  get stateID() {
    return this.id;
  }

  toFirestore() {
    return {
      ...this.data,
    };
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new MileageRate(snapshot.id, data);
  }

  // Editing

  get rules() {
    const isSet = (a) => a !== null && a !== undefined && a !== "";
    return {
      ratePerMile: [
        (a) => isSet(a) || "required",
        (a) => Number(a) > 0 || "ratePositive",
      ],
      effectiveDate: [(a) => isSet(a) || "required"],
    };
  }

  get isValid() {
    const { ratePerMile, effectiveDate } = this.rules;
    return (
      ratePerMile.every((rule) => rule(this.data.ratePerMile) === true) &&
      effectiveDate.every((rule) => rule(this.data.effectiveDate) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, MILEAGE_RATE_FIELDS)));
  }

  isEqual(rate) {
    return Boolean(this.id === rate.id && isEmpty(diff(this.data, rate.data)));
  }

  // Display

  get modifiedDate() {
    return this.data.modified || this.data.created || null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }
}
